// Getting a take's audio to the server, whatever happens on the way:
// register it (retried until the server answers; the same client_ref always
// gets the same upload back), send its parts in order (each one only once it
// is stored on the device; deleted from the device once the server has it),
// then finish it (retried; a repeat is harmless). Nothing here gives up on
// audio: a refusal waits and says why, a 401 waits for a sign-in, a missing
// upload moves the rest of the take to a new one (recorderModel.indexFor), a
// part the server holds other audio for (part_exists) stays on the device to
// be saved as a file, and a part the device could not read is read again
// (only a part the device confirms is gone becomes a gap). A take with no
// audio is never registered nor finished (decisions D4); one Corvus says
// holds no audio while this device still has some is kept here (I1). A take
// that another one carries on is finished only once that one is filed (one
// set of notes for the meeting), and a take of another person who signed in
// on this browser is never sent with this sign-in.

import type { Meeting } from "@/api/meetings";
import type { RecordingStartBody } from "@/api/meetingRecordings";
import { KEEPALIVE_BYTES, backoffMs, indexFor, seqFor, verdictFor, type Verdict } from "./recorderModel";
import type { RecorderStore } from "./recorderStore";
import { failureOf, type EngineDeps, type Runtime } from "./recorderTypes";

export interface UploadHost {
  deps: EngineDeps;
  store(): Promise<RecorderStore>;
  save(rt: Runtime): void;
  changed(): void;
  /** The server has the meeting (registration answered). */
  meetingSeen(rt: Runtime, m: Meeting): void;
  /** The take is finished on the server. */
  finished(rt: Runtime, m: Meeting): void;
  /** The take holds no audio: it leaves the device, never registered or finished. */
  dropped(rt: Runtime): void;
  /** Corvus says it holds no audio for the take (empty_recording): kept here when the device has some. */
  noAudio(rt: Runtime): Promise<void>;
  /** The meeting id of another take in this tab: null while it has none, undefined when it is not here. */
  meetingOf(takeId: string): string | null | undefined;
  /** The take is the signed-in person's (only theirs are sent). */
  owns(rt: Runtime): boolean;
}

const iso = (ms: number) => new Date(ms).toISOString();

/** The take needs nothing more from the uploader. */
const over = (rt: Runtime) => rt.rec.status === "done" || rt.rec.status === "kept";

const WAIT_FOR: Partial<Record<Verdict, number>> = {
  signed_out: 30_000,
  storage_full: 60_000,
  refused: 5 * 60_000,
};

export class Uploader {
  constructor(private host: UploadHost) {}

  private get api() {
    return this.host.deps.api;
  }

  /** Try again later: after the backoff, or the wait a verdict calls for (`local`: this device, not the server, failed). */
  private later(rt: Runtime, verdict: Verdict, local = false): void {
    const clock = this.host.deps.clock;
    if (rt.timer !== null) clock.clearTimeout(rt.timer);
    if (!local) rt.waiting = verdict === "signed_out" || verdict === "storage_full" || verdict === "refused" ? verdict : "offline";
    const ms = WAIT_FOR[verdict] ?? backoffMs(rt.attempt++);
    rt.timer = clock.setTimeout(() => {
      rt.timer = null;
      void this.pump(rt);
    }, ms);
    this.host.changed();
  }

  private settled(rt: Runtime): void {
    rt.bounced = 0;
    if (rt.waiting !== "none" || rt.attempt !== 0) {
      rt.waiting = "none";
      rt.refusal = null;
      rt.attempt = 0;
      this.host.changed();
    }
  }

  /** Register the take, send what is waiting, and finish it once it may. One loop per take at a time. */
  async pump(rt: Runtime): Promise<void> {
    if (rt.sending || over(rt) || !this.host.owns(rt)) return;
    if (this.empty(rt)) return this.host.dropped(rt);
    rt.sending = true;
    let blocked = false;
    try {
      blocked = !(await this.sendAll(rt));
    } finally {
      rt.sending = false;
    }
    // (finished meanwhile by another path: a repeat finish elsewhere)
    if (blocked || over(rt)) return;
    // (the recorder's last word may have come while this loop ran)
    if (this.empty(rt)) return this.host.dropped(rt);
    const now = this.host.deps.clock.now();
    const due = rt.rec.status === "closing" || (rt.rec.status === "held" && rt.rec.finishAt !== null && now >= rt.rec.finishAt);
    if (!due || !rt.finalIn || this.unsent(rt) !== 0) return;
    if (this.carriedOn(rt)) {
      // The take carrying this one on is not filed yet: finishing now would send the meeting for notes mid-recording.
      this.later(rt, "retry", true);
      return;
    }
    await this.finish(rt);
  }

  /** Another take carries this one on (or may, any second) and is not filed yet. */
  private carriedOn(rt: Runtime): boolean {
    if (rt.carryPending) return true;
    const next = rt.rec.finishAfter;
    return !!next && this.host.meetingOf(next) === null;
  }

  /** The microphone went before the first piece: nothing to register, send or finish. */
  private empty(rt: Runtime): boolean {
    return rt.finalIn && rt.rec.status !== "capturing" && rt.rec.nextSeq === 0;
  }

  /** Parts of this take not on the server yet. */
  unsent(rt: Runtime): number {
    return Math.max(0, rt.rec.nextSeq - rt.rec.sentBelow) + rt.resend.length;
  }

  /** False when it stopped to wait (a retry is scheduled). */
  private async sendAll(rt: Runtime): Promise<boolean> {
    for (;;) {
      if (!rt.rec.upload) {
        // Registered with its first piece, never before: an empty take leaves nothing on the server.
        if (rt.rec.nextSeq === 0) return true;
        if (!(await this.register(rt))) return false;
      }
      const upload = rt.rec.upload!;
      let seq: number;
      if (upload.base > 0 && !rt.headerSent) seq = 0;
      else if (rt.resend.length) seq = rt.resend[0];
      else if (rt.rec.sentBelow < rt.rec.nextSeq) seq = rt.rec.sentBelow;
      else return true;
      const blob = await this.partData(rt, seq);
      if (blob === "error") {
        // The device could not read it just now (not "it is not there"): read it again later.
        this.later(rt, "retry", true);
        return false;
      }
      if (!blob) {
        // Confirmed gone from the device (storage was cleared): the server finishes with a gap.
        rt.gaps = true;
        this.advance(rt, seq, 0, false);
        continue;
      }
      try {
        await this.api.put(rt.rec.customerId, rt.rec.meetingId!, upload.uploadId, indexFor(upload, seq), blob, {});
      } catch (e) {
        const { status, code } = failureOf(e);
        const verdict = verdictFor(status, code);
        if (verdict === "move") {
          this.moveAway(rt);
          continue;
        }
        if (verdict === "kept") {
          // The server holds other audio at this index and never replaces it: this part stays here, the rest go on.
          const kept = (rt.rec.keptParts ??= []);
          if (!kept.includes(seq)) kept.push(seq);
          this.host.deps.api.diag(rt.rec.customerId, {
            event: "part_exists", meeting_id: rt.rec.meetingId, upload_id: upload.uploadId, client_ref: rt.rec.id,
            segment: rt.rec.segment, parts: rt.rec.nextSeq, missing: [indexFor(upload, seq)],
          });
          this.advance(rt, seq, 0, false);
          continue;
        }
        if (verdict === "refused") rt.refusal = code ?? `HTTP ${status}`;
        this.later(rt, verdict);
        return false;
      }
      this.settled(rt);
      this.advance(rt, seq, blob.size, true);
    }
  }

  /** The part's audio: from memory, else the device store (a take carried over from a closed page); "error" when the read failed. */
  private async partData(rt: Runtime, seq: number): Promise<Blob | null | "error"> {
    await rt.stored.get(seq)?.catch(() => undefined);
    const inMemory = rt.blobs.get(seq);
    if (inMemory) return inMemory;
    try {
      return (await (await this.host.store()).getPart(rt.rec.id, seq))?.data ?? null;
    } catch {
      return "error";
    }
  }

  /** A part reached the server (`sent`), or is not to be sent: move on; only a sent part is deleted here (part 0 kept for a later move). */
  private advance(rt: Runtime, seq: number, bytes: number, sent: boolean): void {
    const upload = rt.rec.upload;
    if (upload && upload.base > 0 && !rt.headerSent && seq === 0) {
      rt.headerSent = true;
    } else if (rt.resend.length && rt.resend[0] === seq) {
      rt.resend.shift();
    } else {
      rt.rec.sentBelow = Math.max(rt.rec.sentBelow, seq + 1);
    }
    rt.bytesSent += bytes;
    if (sent) rt.unstored.delete(seq);
    if (sent && seq !== 0 && !rt.resend.includes(seq)) {
      rt.blobs.delete(seq);
      rt.stored.delete(seq);
      void this.host.store().then((s) => s.deletePart(rt.rec.id, seq)).catch(() => undefined);
    }
    rt.rec.aliveAt = this.host.deps.clock.now();
    this.host.save(rt);
    this.host.changed();
  }

  /** The server no longer takes parts for this upload: the rest of the take goes to a new one. */
  private moveAway(rt: Runtime): void {
    const old = rt.rec.upload;
    if (old) {
      rt.movedFrom = old.uploadId;
      if (!rt.rec.formerUploads.includes(old.uploadId)) rt.rec.formerUploads.push(old.uploadId);
    }
    rt.rec.upload = null;
    rt.rec.continueRecording = true;
    rt.headerSent = false;
    rt.resend = [];
    this.host.save(rt);
  }

  private async register(rt: Runtime): Promise<boolean> {
    for (let pass = 0; pass < 3; pass++) {
      const r = rt.rec;
      if (r.followsTake && !r.uploads?.length) {
        // After a take not filed yet (a carry-on, or Record again on the same meeting): it goes first, so the
        // meeting's segments keep the order they were recorded in; and a new meeting's id comes from it.
        const id = this.host.meetingOf(r.followsTake);
        if (id === null) {
          this.later(rt, "retry", true);
          return false;
        }
        if (id) r.meetingId = id;
      }
      const body: RecordingStartBody = {
        mime: r.mime || "audio/webm",
        agent: null,
        meeting_id: r.meetingId,
        title: r.meetingId ? null : (r.title || r.meetingTitle || "Meeting"),
        client_ref: r.id,
        continue_recording: r.continueRecording,
        capture_started_at: iso(r.startedAt),
      };
      try {
        const res = await this.api.start(r.customerId, body);
        const known = r.uploads?.find((u) => u.uploadId === res.upload_id);
        if (rt.movedFrom && res.upload_id === rt.movedFrom && (!known || rt.bounced > 0)) {
          // The server handed back the upload it just refused, again: wait rather than loop.
          rt.refusal = "wrong_upload";
          this.later(rt, "refused");
          return false;
        }
        if (rt.movedFrom && res.upload_id === rt.movedFrom) rt.bounced++;
        r.meetingId = res.meeting.id;
        r.meetingTitle = res.meeting.title || r.meetingTitle;
        r.meetingEndAt = res.meeting.end_at ?? r.meetingEndAt;
        r.segment = res.segment ?? 1;
        // An upload this take used before (the same client_ref, still open): carry on with its own
        // numbering, so no index is ever given other audio (decisions D3). Else a new upload: nothing
        // sent yet starts at part 0; otherwise part 0 (the header), then the rest.
        r.upload = known ?? { uploadId: res.upload_id, base: r.sentBelow };
        if (!known) (r.uploads ??= []).push(r.upload);
        rt.headerSent = r.upload.base === 0;
        rt.movedFrom = null;
        this.host.save(rt);
        this.host.meetingSeen(rt, res.meeting);
        this.settled(rt);
        return true;
      } catch (e) {
        const { status, code } = failureOf(e);
        if (code === "already_recorded" && !r.continueRecording) {
          // The meeting has a recording: this take is added to it, never refused.
          r.continueRecording = true;
          continue;
        }
        if ((code === "not_found" || status === 404) && r.meetingId) {
          // The meeting is gone: file the take as a new meeting with its title.
          r.title = r.title || r.meetingTitle || "Meeting";
          r.meetingId = null;
          continue;
        }
        const verdict = verdictFor(status, code);
        if (verdict === "refused" || verdict === "move") rt.refusal = code ?? `HTTP ${status}`;
        this.later(rt, verdict === "move" ? "retry" : verdict);
        return false;
      }
    }
    this.later(rt, "retry");
    return false;
  }

  private async finish(rt: Runtime): Promise<void> {
    if (rt.finishing || !rt.rec.upload || !rt.rec.meetingId) return;
    rt.finishing = true;
    const r = rt.rec;
    try {
      const m = await this.api.finish(r.customerId, r.meetingId!, r.upload!.uploadId, {
        mime: r.mime || null,
        duration_sec: Math.round(r.audioSec),
        allow_gaps: rt.gaps,
        interruptions: r.interruptions.length ? r.interruptions : null,
      });
      this.host.finished(rt, m);
    } catch (e) {
      const { status, code, missing } = failureOf(e);
      if (verdictFor(status, code) === "empty") {
        // Corvus holds no audio for it: final (decisions D4), but audio still here is kept (I1). Never asked twice.
        await this.host.noAudio(rt);
        rt.finishing = false;
        return;
      }
      if (code === "missing_parts") {
        await this.queueMissing(rt, missing ?? []);
        rt.finishing = false;
        void this.pump(rt);
        return;
      }
      if (verdictFor(status, code) === "move") {
        // Not the open upload any more: the server finished it already (or a second device took over).
        if (await this.finishedElsewhere(rt)) return;
      }
      rt.finishing = false;
      const verdict = verdictFor(status, code);
      if (verdict === "refused") rt.refusal = code ?? `HTTP ${status}`;
      this.later(rt, verdict === "move" ? "retry" : verdict);
      return;
    }
    rt.finishing = false;
  }

  /** Send again what the server says it lacks; what the device no longer has becomes a gap. */
  private async queueMissing(rt: Runtime, missing: number[]): Promise<void> {
    const upload = rt.rec.upload!;
    for (const index of missing) {
      const seq = seqFor(upload, index);
      if (rt.rec.keptParts?.includes(seq)) {
        // The server said it held other audio there; it cannot also lack it: never send it again.
        rt.gaps = true;
        continue;
      }
      // A read that fails counts as "still here": the send reads it again; only a confirmed absence is a gap.
      const have = rt.blobs.has(seq) || (await (await this.host.store()).getPart(rt.rec.id, seq).catch(() => "error")) !== null;
      if (have && !rt.resend.includes(seq)) rt.resend.push(seq);
      else if (!have) rt.gaps = true;
    }
    if (!missing.length) rt.gaps = true;
  }

  /** The upload is no longer open on the server: done, if it says the upload is finished. */
  private async finishedElsewhere(rt: Runtime): Promise<boolean> {
    const r = rt.rec;
    try {
      const st = await this.api.uploadState(r.customerId, r.meetingId!, r.upload!.uploadId);
      if (st.state === "open") return false;
    } catch (e) {
      if (failureOf(e).status !== 404) return false;
    }
    try {
      const m = await this.api.meeting(r.customerId, r.meetingId!);
      this.host.finished(rt, m);
      return true;
    } catch {
      return false;
    }
  }

  /** The newest piece as the page goes away: a keepalive PUT when it is next in line and small. */
  sendOnExit(rt: Runtime, seq: number, blob: Blob): void {
    const upload = rt.rec.upload;
    if (!upload || rt.sending || seq !== rt.rec.sentBelow || blob.size > KEEPALIVE_BYTES || !rt.rec.meetingId) return;
    void this.api
      .put(rt.rec.customerId, rt.rec.meetingId, upload.uploadId, indexFor(upload, seq), blob, { keepalive: true })
      .catch(() => undefined);
  }
}
